import cloneDeep from "clone-deep";
import fastDeepEqual from "fast-deep-equal/react.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { accommodationToRooms, createAccommodationRoom, initialOptions, roomsToAccommodation, transformAccommodationValueToText } from "../../../../../helpers/forms/accommodation.js";
import { defaultAccommodationRoom, defaultRestrictions } from "../constants.js";
const useAccommodation = (props)=>{
    const { value , onChange , label , disabled , icon , onBlur , literals , columns  } = props;
    const { adultLabel ='' , adultsLabel ='' , adultsDescription ='' , childLabel ='' , childrenLabel ='' , childrenDescription ='' , agePersonLabel ='' , agesDescription ='' , errorMaxPeople ='' , errorMaxRooms =''  } = literals ?? {};
    const [panelRooms, setPanelRooms] = useState(accommodationToRooms(value?.rooms));
    const [goToLast, setGoToLast] = useState(false);
    useEffect(()=>{
        if (!fastDeepEqual(accommodationToRooms(value?.rooms), panelRooms)) {
            setPanelRooms(accommodationToRooms(value?.rooms));
        }
    }, [
        value,
        panelRooms
    ]);
    const getTotalPeople = useCallback(()=>value?.rooms.map((room)=>room.adults + room.children).reduce((acc, value)=>acc + value, 0) ?? 0, [
        value?.rooms
    ]);
    const errors = useCallback(()=>{
        if (value?.maxPeople && value.maxPeople <= getTotalPeople()) {
            const error = errorMaxPeople.replace('#number#', value.maxPeople.toString());
            return {
                hasError: true,
                error
            };
        }
        if (value?.maxRooms && value.maxRooms <= value?.rooms.length) {
            const error = errorMaxRooms.replace('#number#', value.maxRooms.toString());
            return {
                hasError: true,
                error
            };
        }
        if (!!props.error || props.hasError) {
            return {
                hasError: props.hasError,
                error: props.error
            };
        }
        return {
            hasError: false,
            error: undefined
        };
    }, [
        errorMaxRooms,
        errorMaxPeople,
        value?.maxRooms,
        value?.maxPeople,
        value?.rooms,
        props.hasError,
        props.error,
        getTotalPeople
    ]);
    const onAddRoom = useCallback((e)=>{
        e.preventDefault();
        const { hasError  } = errors();
        if (!hasError) {
            const newValue = cloneDeep(value ?? {
                rooms: []
            });
            const { adults , children  } = createAccommodationRoom(value?.maxPeople, getTotalPeople(), defaultAccommodationRoom);
            newValue.rooms.push({
                ...defaultAccommodationRoom,
                adults,
                children
            });
            onChange?.(newValue);
            setPanelRooms(accommodationToRooms(newValue.rooms));
            setGoToLast(true);
            setTimeout(()=>setGoToLast(false), 200);
        }
    }, [
        value,
        onChange,
        getTotalPeople,
        errors
    ]);
    const onChangeRooms = useCallback((rooms)=>{
        const accommodationRoom = roomsToAccommodation(rooms);
        const newValue = cloneDeep(value ?? {
            rooms: []
        });
        newValue.rooms = accommodationRoom;
        onChange?.(newValue);
        setPanelRooms(rooms);
    }, [
        value,
        onChange
    ]);
    const config = useMemo(()=>({
            literals,
            maxPeople: value?.maxPeople,
            maxRooms: value?.maxRooms,
            columns
        }), [
        value?.maxPeople,
        value?.maxRooms,
        literals,
        columns
    ]);
    const people = useMemo(()=>[
            {
                personLabel: adultLabel,
                personsLabel: adultsLabel,
                personDescription: adultsDescription,
                min: value?.adults?.min ?? defaultRestrictions.adults.min,
                max: value?.adults?.max ?? defaultRestrictions.adults.max,
                value: value?.adults?.value ?? defaultRestrictions.adults.value
            },
            {
                personLabel: childLabel,
                personsLabel: childrenLabel,
                personDescription: childrenDescription,
                labelForEachOption: agePersonLabel,
                min: value?.children?.min ?? defaultRestrictions.children.min,
                max: value?.children?.max ?? defaultRestrictions.children.max,
                value: value?.children?.value ?? defaultRestrictions.children.value,
                optionsPersonLabel: agesDescription,
                options: initialOptions(value?.children?.maxAge ?? defaultRestrictions.children.maxAge, value?.children?.defaultAge)
            }
        ], [
        adultLabel,
        adultsLabel,
        adultsDescription,
        value?.adults?.min,
        value?.adults?.max,
        value?.adults?.value,
        value?.children?.min,
        value?.children?.max,
        value?.children?.value,
        value?.children?.maxAge,
        value?.children?.defaultAge,
        childLabel,
        childrenLabel,
        childrenDescription,
        agePersonLabel,
        agesDescription
    ]);
    const inputProps = useMemo(()=>({
            label,
            value: value ? transformAccommodationValueToText(value, literals) : '',
            disabled,
            icon,
            onBlur
        }), [
        disabled,
        icon,
        label,
        literals,
        value,
        onBlur
    ]);
    return {
        panelRooms,
        config,
        people,
        goToLast,
        inputProps,
        ...errors(),
        onAddRoom,
        onChangeRooms
    };
};
export default useAccommodation;

//# sourceMappingURL=useAccommodation.js.map